import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Image, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useMoreOptionsData } from '../hooks/useMoreOptionsData';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

/** Desktop-web account menu — a true anchored dropdown under the header
 * avatar, consuming the same useMoreOptionsData() hook that drives
 * MoreOptionsModal (native + mobile-web's full-screen equivalent) so both
 * surfaces share one source of truth for business-loading/image-fallback
 * logic instead of drifting independently. */
export const AvatarMenu: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<View>(null);
  const rtl = useRTLStyles();
  const { t } = useLocale();

  const close = () => setOpen(false);
  const {
    user,
    currentBusiness,
    userBusinesses,
    businessRole,
    isLoadingBusinesses,
    imageError,
    setImageError,
    moreOptions,
    switchBusiness,
    createBusiness,
  } = useMoreOptionsData(close, open);

  // Click-outside-to-close — web only, standard document listener pattern
  // (no built-in click-outside handling exists in this codebase's Dropdown
  // component; that's left to callers there too).
  useEffect(() => {
    if (Platform.OS !== 'web' || !open) return;
    const handleClick = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && e.target instanceof Node && !node.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const profilePictureUrl = user?.avatar || user?.profilePicture;
  const hasValidProfilePicture = !imageError && profilePictureUrl && typeof profilePictureUrl === 'string' &&
    profilePictureUrl.trim() !== '' && profilePictureUrl !== 'null' && profilePictureUrl !== 'undefined' &&
    (profilePictureUrl.startsWith('http://') || profilePictureUrl.startsWith('https://') ||
     profilePictureUrl.startsWith('file://') || profilePictureUrl.startsWith('data:'));

  const firstName = user?.firstName || user?.name?.split(' ')[0] || '';
  const lastName = user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '';
  const initials = ((firstName ? firstName.charAt(0).toUpperCase() : '') + (lastName ? lastName.charAt(0).toUpperCase() : '')) || 'U';

  return (
    <View ref={containerRef} style={styles.wrapper}>
      <TouchableOpacity style={styles.avatarButton} onPress={() => setOpen((v) => !v)} activeOpacity={0.75}>
        {hasValidProfilePicture ? (
          <Image source={{ uri: profilePictureUrl }} style={styles.avatarImage} onError={() => setImageError(true)} />
        ) : (
          <Text style={styles.avatarInitials}>{initials}</Text>
        )}
      </TouchableOpacity>

      {open && (
        <View style={[styles.panel, rtl.isRTL ? styles.panelRTL : styles.panelLTR]}>
          <View style={styles.panelHeader}>
            <Text style={[styles.userName, { textAlign: rtl.textStart }]} numberOfLines={1}>
              {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : (user?.firstName || t('avatarMenu.fallbackUserName'))}
            </Text>
            <Text style={[styles.userEmail, { textAlign: rtl.textStart }]} numberOfLines={1}>{user?.email || ''}</Text>
          </View>

          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
            {isLoadingBusinesses ? (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('avatarMenu.loadingBusinesses')}</Text>
              </View>
            ) : userBusinesses.length > 0 ? (
              <View style={styles.section}>
                <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('avatarMenu.business')}</Text>
                <TouchableOpacity style={[styles.businessRow, { flexDirection: rtl.rowDirection }]} onPress={switchBusiness}>
                  <Ionicons name="business" size={16} color={colors.brand.primary as string} />
                  <View style={styles.businessInfo}>
                    <Text style={[styles.businessName, { textAlign: rtl.textStart }]} numberOfLines={1}>
                      {currentBusiness?.name || userBusinesses[0]?.name || t('avatarMenu.fallbackBusinessName')}
                    </Text>
                    <Text style={[styles.businessRole, { textAlign: rtl.textStart }]}>{businessRole || t('avatarMenu.role.owner')}</Text>
                  </View>
                  <Ionicons name={rtl.mirrorIcon('swap-horizontal') as any} size={16} color={colors.text.secondary as string} />
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.section}>
                <TouchableOpacity style={[styles.businessRow, { flexDirection: rtl.rowDirection }]} onPress={createBusiness}>
                  <Ionicons name="add-circle-outline" size={16} color={colors.brand.primary as string} />
                  <Text style={[styles.createBusinessText, { textAlign: rtl.textStart }]}>{t('avatarMenu.createBusiness')}</Text>
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.divider} />

            <View style={styles.section}>
              {moreOptions.map((option) => (
                <TouchableOpacity key={option.id} style={[styles.optionRow, { flexDirection: rtl.rowDirection }]} onPress={option.action} activeOpacity={0.7}>
                  <Ionicons name={option.icon as any} size={16} color={colors.text.secondary as string} />
                  <Text style={[styles.optionText, { textAlign: rtl.textStart }]}>{option.title}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  avatarButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primaryTransparent,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  avatarInitials: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.bold,
    color: colors.brand.primary,
  },
  panel: {
    position: 'absolute',
    top: '100%',
    marginTop: spacing.xs,
    width: 280,
    maxHeight: 480,
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    ...designTokens.shadows.lg,
    zIndex: 2000,
  },
  // Anchor to whichever edge keeps the panel on-screen — see
  // NotificationsDropdown.tsx's identical comment for the RTL rationale.
  panelLTR: {
    right: 0,
  },
  panelRTL: {
    left: 0,
  },
  panelHeader: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  userName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
  },
  userEmail: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
  scroll: {
    maxHeight: 420,
  },
  scrollContent: {
    paddingVertical: spacing.xs,
  },
  section: {
    paddingHorizontal: spacing.sm,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    paddingHorizontal: spacing.xs,
    paddingTop: spacing.xs,
    paddingBottom: 2,
  },
  businessRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
  },
  businessInfo: {
    flex: 1,
    minWidth: 0,
  },
  businessName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  businessRole: {
    fontSize: 11,
    color: colors.text.secondary,
    textTransform: 'capitalize',
  },
  createBusinessText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.brand.primary,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.xs,
    marginHorizontal: spacing.sm,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
  },
  optionText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
  },
});
